import { fetchBookedTimes } from "./timeDropdown.js";

/**
 * Initialize the date selectors (month and year) and the calendar grid.
 * @param {HTMLSelectElement} monthSelect - The month dropdown element.
 * @param {HTMLSelectElement} yearSelect - The year dropdown element.
 * @param {HTMLElement} calendarGrid - The calendar grid container element.
 * @param {Function} generateCalendar - The function to regenerate the calendar grid.
 */
export function initializeDateSelectors(monthSelect, yearSelect, calendarGrid, generateCalendar) {
    console.log('Initializing calendar...');

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    populateDropdown(yearSelect, generateYearOptions(currentYear, currentYear + 10));
    yearSelect.value = currentYear;

    populateDropdown(
        monthSelect,
        generateMonthOptions(currentYear, currentYear, currentMonth)
    );
    monthSelect.value = currentMonth;

    generateCalendar(currentYear, currentMonth, calendarGrid, today);

    const updateHandler = () => updateCalendar(yearSelect, monthSelect, calendarGrid, generateCalendar, today);
    monthSelect.addEventListener("change", updateHandler);
    yearSelect.addEventListener("change", updateHandler);
}

/**
 * Populate a dropdown element with options.
 * @param {HTMLSelectElement} select - The dropdown element to populate.
 * @param {Array} options - Array of { value, text, disabled } objects for the dropdown.
 */
function populateDropdown(select, options) {
    select.innerHTML = "";
    options.forEach(({ value, text, disabled }) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = text;
        if (disabled) option.disabled = true;
        select.appendChild(option);
    });
}

/**
 * Generate options for the month dropdown, disabling past months only for the current year.
 * @param {number} selectedYear - The year selected in the dropdown.
 * @param {number} currentYear - The current year.
 * @param {number} currentMonth - The current month (0-11).
 * @returns {Array} Array of { value, text, disabled } objects representing months.
 */
function generateMonthOptions(selectedYear, currentYear, currentMonth) {
    return Array.from({ length: 12 }, (_, i) => ({
        value: i,
        text: new Date(0, i).toLocaleString("default", { month: "long" }),
        disabled: selectedYear === currentYear && i < currentMonth, 
        // Disable past months only for the current year
    }));
}

/**
 * Generate options for the year dropdown, disabling past years.
 * @param {number} startYear - The starting year.
 * @param {number} endYear - The ending year.
 * @returns {Array} Array of { value, text, disabled } objects representing years.
 */
function generateYearOptions(startYear, endYear) {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => ({
        value: startYear + i,
        text: startYear + i,
        disabled: startYear + i < currentYear,
    }));
}

/**
 * Generate the calendar and disable weekends and public holidays.
 * @param {number} year - The year.
 * @param {number} month - The month (0-based).
 * @param {HTMLElement} calendarGrid - The calendar grid container element.
 * @param {Date} today - Today's date for reference.
 */
export async function generateCalendar(year, month, calendarGrid, today) {
    const spinner = document.getElementById("calendar-spinner");
    // const publicHolidays = await fetchPublicHolidays(year); 
    // Fetch the public holidays

    spinner.classList.remove("hidden");

    try {
        calendarGrid.innerHTML = "";
        calendarGrid.dataset.year = year;
        calendarGrid.dataset.month = month;

        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = new Date(year, month, 1).getDay();

        // Add empty divs for the days before the first day of the month
        calendarGrid.append(...Array.from({ length: firstDay }, () => document.createElement("div")));

        const bookingStatuses = await fetchBookingStatuses(year, month);

        for (let day = 1; day <= daysInMonth; day++) {
            const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            createDayCell(calendarGrid, day, date, today, bookingStatuses[day]);
        }
    } catch (error) {
        console.error("Error generating calendar:", error);
    } finally {
        spinner.classList.add("hidden");
    }
}

/**
 * Fetch public holidays for the given year.
 * @param {number} year - The year for which to fetch public holidays.
 * @returns {Promise<Set>} A set of public holiday dates in YYYY-MM-DD format.
 */
async function fetchPublicHolidays(year) {
    const publicHolidays = new Set();

    try {
        const response = await fetch(`/wp-json/holidays/v1/get-public-holidays/${year}`);
        const data = await response.json();

        data.forEach(holiday => {
            publicHolidays.add(holiday.date); 
            // Assuming the response includes a 'date' field in YYYY-MM-DD format
        });
    } catch (error) {
        console.error("Error fetching public holidays:", error);
    }

    return publicHolidays;
}

/**
 * Fetch booking statuses for all days in a month.
 * @param {number} year - The year.
 * @param {number} month - The month (0-based).
 * @returns {Promise<Object>} A mapping of day numbers to booking statuses.
 */
async function fetchBookingStatuses(year, month) {
    const daysInMonth = getDaysInMonth(year, month);
    const bookingStatuses = {};

    await Promise.all(
        Array.from({ length: daysInMonth }, async (_, index) => {
            const day = index + 1;
            const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

            try {
                const response = await fetch(`/wp-json/booking/v1/is-fully-booked/${date}`);
                const data = await response.json();
                bookingStatuses[day] = data;
            } catch (error) {
                console.error(`Error fetching booking status for ${date}:`, error);
            }
        })
    );

    return bookingStatuses;
}

function createDayCell(calendarGrid, day, date, today, bookingStatus) {
    console.log(bookingStatus, 'booking status');
    const dayDiv = document.createElement("div");
    dayDiv.textContent = day;
    // Base glass styling: translucent background, blur, and subtle border
    dayDiv.className = "p-2 cursor-pointer rounded-lg transition-all duration-200 bg-white/20 backdrop-blur-md border border-white/30 hover:bg-purple-400/30 hover:shadow-lg";

    const dayDate = new Date(date);
    // Normalize today's date to avoid time-related discrepancies
    const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6; // Saturday or Sunday
    const isPastDate = dayDate < normalizedToday && dayDate.toDateString() !== normalizedToday.toDateString();

    if (isWeekend || isPastDate) {
        dayDiv.classList.add("cursor-not-allowed", "text-gray-400");
        dayDiv.style.pointerEvents = "none";
    } else {
        if (bookingStatus?.isFullyBooked) {
            // For fully booked days, override with a red glass effect overlay
            dayDiv.classList.remove("bg-white/20", "hover:bg-white/30");
            dayDiv.classList.add("bg-red-400/70", "text-white");
            dayDiv.style.pointerEvents = "none";
        } else if (bookingStatus?.isPartiallyBooked) {
            // For partially booked days, use an orange glass overlay
            dayDiv.classList.remove("bg-white/20", "hover:bg-white/30");
            dayDiv.classList.add("bg-orange-400/70", "text-white");
            dayDiv.addEventListener("click", () => selectDate(dayDiv, day, calendarGrid));
            dayDiv.addEventListener("mouseover", () => showBookings(dayDiv, dayDate));
            dayDiv.addEventListener("mouseout", () => hideBookings(dayDiv, dayDate));
        } else {
            // Highlight today's date with a blue glass overlay
            dayDiv.classList.remove("bg-white/20", "hover:bg-white/30");
            if (dayDate.toDateString() === normalizedToday.toDateString()) {
                dayDiv.classList.add("bg-blue-400/70", "text-white");
            }
            dayDiv.addEventListener("click", () => selectDate(dayDiv, day, calendarGrid));
            dayDiv.addEventListener("mouseover", () => showBookings(dayDiv, dayDate));
            dayDiv.addEventListener("mouseout", () => hideBookings(dayDiv, dayDate));
        }
    }

    calendarGrid.appendChild(dayDiv);
}


/**
 * Show the list of bookings for the day.
 * @param {HTMLElement} dayCell - The day cell element.
 * @param {Array} dayDate- The list of bookings for the day.
 */
async function showBookings(dayDiv, dayDate) {

    // Extract year, month, and day
    const year = dayDate.getFullYear();
    const month = dayDate.getMonth() + 1; // getMonth() is 0-based
    const day = dayDate.getDate();

    // Format the month and day to have leading zeros if necessary
    const formattedMonth = String(month).padStart(2, "0");
    const formattedDay = String(day).padStart(2, "0");

    // Combine into the desired format
    const selectedDate = `${year}-${formattedMonth}-${formattedDay}`;

    console.log("Selected Date:", selectedDate);
    const bookedTimes = await fetchBookedTimes(selectedDate);
    console.log(bookedTimes);
    // const bookingsList = document.createElement("ul");
    // bookingsList.classList.add("bookings-list");

    // bookings.forEach(booking => {
    //     const bookingItem = document.createElement("li");
    //     bookingItem.textContent = booking;
    //     bookingsList.appendChild(bookingItem);
    // });

    // dayDiv.appendChild(bookingsList);
}

/**
 * Hide the list of bookings for the day.
 * @param {HTMLElement} dayCell - The day cell element.
 */
function hideBookings(dayDiv) {
    const bookingsList = dayDiv.querySelector(".bookings-list");
    if (bookingsList) {
        dayDiv.removeChild(bookingsList);
    }
}

/**
 * Get the number of days in a given month of a year.
 * @param {number} year - The year.
 * @param {number} month - The month (0-11).
 * @returns {number} The number of days in the specified month.
 */
function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

/**
 * Handle selecting a date in the calendar.
 * @param {HTMLElement} selectedDiv - The div element of the selected date.
 * @param {number} day - The selected day.
 * @param {HTMLElement} calendarGrid - The calendar grid container element.
 */
export function selectDate(selectedDiv, day, calendarGrid) {
    clearPreviousSelection(calendarGrid);
    highlightSelectedDate(selectedDiv);

    const correctedMonth = parseInt(calendarGrid.dataset.month, 10) + 1;

    const selectedDateEvent = new CustomEvent("dateSelected", {
        detail: {
            day: day,
            month: correctedMonth,
            year: calendarGrid.dataset.year,
        },
    });
    document.dispatchEvent(selectedDateEvent);

    console.log(`Selected Date: ${day}-${correctedMonth}-${calendarGrid.dataset.year}`);
}

/**
 * Clear the previous date selection from the calendar grid.
 * @param {HTMLElement} calendarGrid - The calendar grid container element.
 */
function clearPreviousSelection(calendarGrid) {
    [...calendarGrid.children].forEach(div => div.classList.remove("bg-purple-600", "text-white"));
}

/**
 * Highlight the currently selected date.
 * @param {HTMLElement} dayDiv - The div element of the selected date.
 */
function highlightSelectedDate(dayDiv) {
    dayDiv.classList.add("bg-purple-600", "text-white");
}

/**
 * Update the calendar based on the selected year and month.
 * @param {HTMLSelectElement} yearSelect - The year dropdown element.
 * @param {HTMLSelectElement} monthSelect - The month dropdown element.
 * @param {HTMLElement} calendarGrid - The calendar grid container element.
 * @param {Function} generateCalendar - The function to regenerate the calendar grid.
 * @param {Date} today - Today's date for reference.
 */
function updateCalendar(yearSelect, monthSelect, calendarGrid, generateCalendar, today) {
    const selectedYear = parseInt(yearSelect.value, 10);
    const selectedMonth = parseInt(monthSelect.value, 10);

    // Regenerate the month options to ensure correct disabling
    populateDropdown(
        monthSelect,
        generateMonthOptions(selectedYear, today.getFullYear(), today.getMonth())
    );
    monthSelect.value = selectedMonth;

    // Call the function to regenerate the calendar
    generateCalendar(selectedYear, selectedMonth, calendarGrid, today);
}
